import { ActionModal, FormLayout, TextInput } from '@kcftech/design-system';
import { IndustryCreateRequest } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { errorMessage, industryNameMaxLength } from 'pages/Settings/Industries/shared';
import { RegisterOptions, useForm } from 'react-hook-form';
import { Filter } from 'utils/query_v3';

const nameRules: RegisterOptions = {
    required: 'Please enter the industry name',
    maxLength: {
        value: industryNameMaxLength,
        message: errorMessage,
    },
};

const validateName = async (value: string) => {
    const industryNameFiltered = await sdClient.getIndustries({
        filter: Filter.eq('Name', value),
    });

    if (industryNameFiltered.Result.length >= 1) {
        return 'Industry already exists. Please enter a unique name.';
    }

    // Check if the name is empty
    if (value.replaceAll(' ', '').length === 0) {
        return 'Industry name is required.';
    }

    return undefined;
};

export type CreateIndustriesModalProps = Readonly<{
    onCreate: () => void;
    onClose: () => void;
}>;
type IndustryFormData = Readonly<{
    Name: string;
}>;
export function CreateIndustriesModal({ onCreate, onClose }: CreateIndustriesModalProps) {
    const { showToast } = useNotificationContext();
    const {
        register,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
        handleSubmit,
    } = useForm<IndustryFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            Name: '',
        },
    });
    const { mutate: createIndustry, isPending } = useMutation({
        mutationFn: async (data: IndustryFormData) => {
            await sdClient.createIndustry({
                body: new IndustryCreateRequest({ Name: data.Name }),
            });
        },

        onSuccess: () => {
            showToast({
                type: 'success',
                content: 'Successfully created industry',
            });
            onCreate();
        },

        onError: () => showToast({ type: 'error', content: 'Failed to create industry' }),
    });

    const onSave = handleSubmit((formData) =>
        createIndustry(
            new IndustryCreateRequest({
                Name: formData.Name.trim(),
            }),
        ),
    );

    return (
        <ActionModal
            onAction={onSave}
            actionable={
                (!isSubmitted || isValid) && !(isValidating || isSubmitting || isPending)
            }
            onClose={onClose}
            title="Create Industry"
            actionButtonText="Create"
            closeButtonText="Cancel"
            height={350}
        >
            <FormLayout>
                <TextInput
                    label="Industry Name"
                    error={errors.Name?.message}
                    required
                    placeholder="Enter industry name"
                    {...register('Name', { ...nameRules, validate: validateName })}
                />
            </FormLayout>
        </ActionModal>
    );
}
